import { useState, useEffect } from "react";
import { sanitizeInput, trimInput } from "src/helpers/sanitizeInput";
import { APP_INFO } from "src/config/appInfo";
import { apiRequest } from "src/services/apiClient";
import { SettingRow, Section } from "./SettingsControls";
import { useTranslation } from "react-i18next";
import { useAuth } from "src/context/AuthProvider";
import { useEscKey } from "src/hooks/useEscKey";
import WhatsNewPopup from "src/components/WhatsNewPopup/WhatsNewPopup";
import { fetchCurrentAppVersion } from "src/services/appVersion";

const BUG_AREA_KEYS = [
    "bugReport.areaInvoices",
    "bugReport.areaPatients",
    "bugReport.areaEmail",
    "bugReport.areaPortal",
    "bugReport.areaSettings",
    "bugReport.areaBilling",
    "bugReport.areaOther",
];

export function ZaalaSettings() {
    const { t } = useTranslation();
    const { user } = useAuth();

    // Referral
    const [referOpen, setReferOpen] = useState(false);
    const [referCopied, setReferCopied] = useState(false);
    const [showWhatsNew, setShowWhatsNew] = useState(false);
    // Displayed version follows whatever's been published in admin, not the build
    // constant — falls back to APP_INFO.version until something's ever been published.
    const [displayVersion, setDisplayVersion] = useState(APP_INFO.version);
    useEffect(() => {
        fetchCurrentAppVersion().then((v) => { if (v) setDisplayVersion(v.version); });
    }, []);

    const doctorDisplay = [user?.doctor.title, user?.doctor.firstName, user?.doctor.lastName].filter(Boolean).join(" ");
    const praxisDisplay = user?.praxis.name ?? "unserer Praxis";

    const referSubject = encodeURIComponent(t("referral.subject"));
    const referBody = `Liebe Kollegin, lieber Kollege,\n\nwir nutzen Medinvoice für die Privatliquidation und können es wärmstens empfehlen.\n\nDas Tool übernimmt den gesamten Rechnungsprozess: Import der Rechnungsdatei (PAD/PADNEXT), digitaler Rechnungsversand an Patienten, automatisches Mahnwesen und Zahlungsabgleich. Patienten erhalten ihre Rechnung per Link (DSGVO-konform) und können direkt aus dem Portal heraus die Rechnung einsehen bzw. herunterladen und über den QR-Code mittels banking-App bezahlen.\n\nWeitere Informationen: https://www.medinvoice.io\n\nMit kollegialen Grüßen\n\n${doctorDisplay}${praxisDisplay ? `\n${praxisDisplay}` : ""}`;

    const handleReferCopy = () => {
        navigator.clipboard.writeText(referBody).then(() => {
            setReferCopied(true);
            setTimeout(() => setReferCopied(false), 2500);
        });
    };

    // Bug report
    const [bugOpen, setBugOpen] = useState(false);
    const [bugTitle, setBugTitle] = useState("");
    const [bugDesc, setBugDesc] = useState("");
    const [bugArea, setBugArea] = useState("");
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [bugError, setBugError] = useState<string | null>(null);

    const closeBug = () => { setBugOpen(false); setBugTitle(""); setBugDesc(""); setBugArea(""); setSent(false); setBugError(null); };

    const handleSend = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!bugTitle.trim() || !bugDesc.trim() || !bugArea) {
            setBugError(t("settings.errorBugTitleDesc"));
            return;
        }
        setSending(true);
        setBugError(null);
        try {
            await apiRequest("/bug-report", {
                method: "POST",
                body: JSON.stringify({
                    title: bugTitle,
                    description: bugDesc,
                    area: bugArea,
                    version: APP_INFO.version,
                }),
            }, { "Content-Type": "application/json" });
            setSent(true);
        } catch {
            setBugError(t("settings.errorFailedSend"));
        } finally {
            setSending(false);
        }
    };

    // Suggestion
    const [sugOpen, setSugOpen] = useState(false);
    const [sugTitle, setSugTitle] = useState("");
    const [sugDesc, setSugDesc] = useState("");
    const [sugSending, setSugSending] = useState(false);
    const [sugSent, setSugSent] = useState(false);
    const [sugError, setSugError] = useState<string | null>(null);

    const closeSug = () => { setSugOpen(false); setSugTitle(""); setSugDesc(""); setSugSent(false); setSugError(null); };

    useEscKey(() => setReferOpen(false), referOpen);
    useEscKey(closeBug, bugOpen);
    useEscKey(closeSug, sugOpen);

    const handleSugSend = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!sugTitle.trim()) {
            setSugError(t("settings.errorBugTitleDesc"));
            return;
        }
        setSugSending(true);
        setSugError(null);
        try {
            await apiRequest("/bug-report", {
                method: "POST",
                body: JSON.stringify({
                    title: sugTitle,
                    description: sugDesc || "—",
                    area: "Suggestion",
                    version: APP_INFO.version,
                    type: "suggestion",
                }),
            }, { "Content-Type": "application/json" });
            setSugSent(true);
        } catch {
            setSugError(t("settings.errorFailedSend"));
        } finally {
            setSugSending(false);
        }
    };

    return (
        <Section title={t("settings.zaala")} logo actions={
            <>
                <button className="create-modal__btn create-modal__btn--ghost" onClick={() => setReferOpen(true)}>
                    {t("referral.button")}
                </button>
                <button className="create-modal__btn create-modal__btn--ghost" onClick={() => setSugOpen(true)}>
                    {t("suggestion.helpUsImprove")}
                </button>
                <button className="create-modal__btn create-modal__btn--submit" onClick={() => setBugOpen(true)}>
                    {t("bugReport.reportBug")}
                </button>
            </>
        }>
            <SettingRow title={t("settings.company")}>{APP_INFO.legalName}</SettingRow>
            <SettingRow title={t("settings.taxNumber")}>{APP_INFO.taxNumber}</SettingRow>
            <SettingRow title={t("settings.vatId")}>{APP_INFO.vatId}</SettingRow>
            <SettingRow title={t("form.email")}>
                <a href={`mailto:${APP_INFO.email}`}>{APP_INFO.email}</a>
            </SettingRow>
            <SettingRow title={t("settings.version")}>
                v{displayVersion}{" "}
                <button className="whats-new-link" onClick={() => setShowWhatsNew(true)}>
                    {t("whatsNew.button")}
                </button>
            </SettingRow>

            {showWhatsNew && <WhatsNewPopup onClose={() => setShowWhatsNew(false)} />}

            {referOpen && (
                <div className="create-modal-overlay" onClick={() => setReferOpen(false)}>
                    <div className="create-modal" onClick={(e) => e.stopPropagation()}>
                        <h2 className="create-modal__title">{t("referral.modalTitle")}</h2>
                        <p className="create-modal__lead">{t("referral.modalLead")}</p>
                        <div className="create-modal__field">
                            <label htmlFor="refer-body">{t("referral.messageLabel")}</label>
                            <textarea
                                id="refer-body"
                                rows={10}
                                readOnly
                                value={referBody}
                                
                            />
                        </div>
                        <div className="create-modal__actions">
                            <button type="button" className="create-modal__btn create-modal__btn--cancel" onClick={() => setReferOpen(false)}>
                                {t("referral.close")}
                            </button>
                            <button type="button" className="create-modal__btn create-modal__btn--ghost" onClick={handleReferCopy}>
                                {referCopied ? t("referral.copied") : t("referral.copy")}
                            </button>
                            <a
                                className="create-modal__btn create-modal__btn--submit"
                                href={`mailto:?subject=${referSubject}&body=${encodeURIComponent(referBody)}`}
                                target="_blank"
                                rel="noreferrer"
                            >
                                {t("referral.openEmail")}
                            </a>
                        </div>
                    </div>
                </div>
            )}

            {bugOpen && (
                <div className="create-modal-overlay" onClick={closeBug}>
                    <div className="create-modal" onClick={(e) => e.stopPropagation()}>
                        <h2 className="create-modal__title">{t("bugReport.reportBug")}</h2>
                        {sent ? (
                            <>
                                <p style={{ margin: "16px 0" }}>{t("bugReport.thankYou")}</p>
                                <div className="create-modal__actions">
                                    <button className="create-modal__btn create-modal__btn--submit" onClick={closeBug}>{t("bugReport.close")}</button>
                                </div>
                            </>
                        ) : (
                            <form className="create-modal__form" onSubmit={handleSend}>
                                <div className="create-modal__field">
                                    <label htmlFor="bug-area">{t("bugReport.bugArea")}</label>
                                    <select
                                        id="bug-area"
                                        value={bugArea}
                                        onChange={(e) => setBugArea(e.target.value)}
                                    >
                                        <option value="">{t("bugReport.bugAreaPlaceholder")}</option>
                                        {BUG_AREA_KEYS.map((k) => <option key={k} value={t(k)}>{t(k)}</option>)}
                                    </select>
                                </div>
                                <div className="create-modal__field">
                                    <label htmlFor="bug-title">{t("bugReport.bugTitle")}</label>
                                    <input
                                        id="bug-title"
                                        name="title"
                                        value={bugTitle}
                                        onChange={(e) => setBugTitle(sanitizeInput(e.target.value))}
                                        onBlur={(e) => setBugTitle(trimInput(e.target.value))}
                                        placeholder={t("bugReport.bugTitlePlaceholder")}
                                        autoFocus
                                    />
                                </div>
                                <div className="create-modal__field">
                                    <label htmlFor="bug-description">{t("bugReport.bugDescription")}</label>
                                    <textarea
                                        id="bug-description"
                                        name="description"
                                        value={bugDesc}
                                        onChange={(e) => setBugDesc(e.target.value)}
                                        placeholder={t("bugReport.bugDescriptionPlaceholder")}
                                        rows={5}
                                        
                                    />
                                </div>
                                {bugError && <p className="create-modal__error">{bugError}</p>}
                                <div className="create-modal__actions">
                                    <button type="button" className="create-modal__btn create-modal__btn--cancel" onClick={closeBug}>{t("bugReport.cancel")}</button>
                                    <button type="submit" className="create-modal__btn create-modal__btn--submit" disabled={sending}>
                                        {sending ? t("bugReport.sending") : t("bugReport.sendReport")}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            )}

            {sugOpen && (
                <div className="create-modal-overlay" onClick={closeSug}>
                    <div className="create-modal" onClick={(e) => e.stopPropagation()}>
                        <h2 className="create-modal__title">{t("suggestion.modalTitle")}</h2>
                        {sugSent ? (
                            <>
                                <p style={{ margin: "16px 0" }}>{t("suggestion.thankYou")}</p>
                                <div className="create-modal__actions">
                                    <button className="create-modal__btn create-modal__btn--submit" onClick={closeSug}>{t("bugReport.close")}</button>
                                </div>
                            </>
                        ) : (
                            <form className="create-modal__form" onSubmit={handleSugSend}>
                                <p className="create-modal__lead">{t("suggestion.modalLead")}</p>
                                <div className="create-modal__field">
                                    <label htmlFor="sug-title">{t("suggestion.titleLabel")}</label>
                                    <input
                                        id="sug-title"
                                        value={sugTitle}
                                        onChange={(e) => setSugTitle(sanitizeInput(e.target.value))}
                                        onBlur={(e) => setSugTitle(trimInput(e.target.value))}
                                        placeholder={t("suggestion.titlePlaceholder")}
                                        autoFocus
                                    />
                                </div>
                                <div className="create-modal__field">
                                    <label htmlFor="sug-desc">{t("suggestion.descLabel")}</label>
                                    <textarea
                                        id="sug-desc"
                                        value={sugDesc}
                                        onChange={(e) => setSugDesc(e.target.value)}
                                        placeholder={t("suggestion.descPlaceholder")}
                                        rows={5}
                                        
                                    />
                                </div>
                                {sugError && <p className="create-modal__error">{sugError}</p>}
                                <div className="create-modal__actions">
                                    <button type="button" className="create-modal__btn create-modal__btn--cancel" onClick={closeSug}>{t("suggestion.cancel")}</button>
                                    <button type="submit" className="create-modal__btn create-modal__btn--submit" disabled={sugSending}>
                                        {sugSending ? t("suggestion.sending") : t("suggestion.send")}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            )}
        </Section>
    );
}
